import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import type { SingleUnit } from '../../types/schema';
import type { tGroupedEquipment } from '../../types';
import { Btn, Card } from '../ui';
import {
    IconCalendar,
    IconClipboard,
    IconForms,
    IconReports,
} from '@icons';
import { ITEM_STATUS_CODES } from '../../utils/constants';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import type { UnitOfMeasureOption } from '../../utils/unitOfMeasureUtils';
import {
    formatInspectionTimestamp,
    formatUmReading,
    INSPECTION_OPERATIONAL_STATUS_LABEL,
    resolveOperationalStatus,
    resolveUmSinceLastService,
    type InspectionOperationalStatus,
} from './inspectionFlowUtils';

type InspectionActionTone = 'navy' | 'coral' | 'blue' | 'green';

type InspectionActionCardProps = {
    title: string;
    description: string;
    icon: ReactNode;
    tone: InspectionActionTone;
    disabled?: boolean;
    onClick: () => void;
};

const InspectionActionCard = ({
    title,
    description,
    icon,
    tone,
    disabled,
    onClick,
}: InspectionActionCardProps) => (
    <Card
        variant="card-sm"
        className={clsx('inspection-flow-action-card card-pad-md', disabled && 'is-disabled')}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={() => {
            if (!disabled) {
                onClick();
            }
        }}
        onKeyDown={(event) => {
            if (disabled) {
                return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
            }
        }}
    >
        <span className={clsx('inspection-flow-action-icon', `tone-${tone}`)} aria-hidden="true">{icon}</span>
        <span className="inspection-flow-action-body">
            <span className="inspection-flow-action-title">{title}</span>
            <span className="inspection-flow-action-description">{description}</span>
        </span>
        <span className="inspection-flow-action-chevron" aria-hidden="true">›</span>
    </Card>
);

type InspectionOptionsHubProps = {
    equipmentData: tGroupedEquipment;
    selectedUnit: SingleUnit;
    imageUrl?: string;
    categoryLabel: string;
    locationLabel: string;
    lastInspectedAt: string | null;
    lastInspectorName: string | null;
    openIssueCount: number;
    primaryDiscrepancyStatus: string | null;
    umCatalog: UnitOfMeasureOption[];
    hasDailyServiceForm: boolean;
    canViewLastReport: boolean;
    isOutOfService: boolean;
    onBeginInspection: () => void;
    onRecordDailyService: () => void;
    onViewLastReport: () => void;
    onViewReportHistory: () => void;
    onChangeUnit?: () => void;
    showChangeUnit?: boolean;
};

const operationalStatusClass: Record<InspectionOperationalStatus, string> = {
    active: 'status-active',
    under_review: 'status-under-review',
    out_of_service: 'status-out-of-service',
};

export const InspectionOptionsHub = ({
    equipmentData,
    selectedUnit,
    imageUrl,
    categoryLabel,
    locationLabel,
    lastInspectedAt,
    lastInspectorName,
    openIssueCount,
    primaryDiscrepancyStatus,
    umCatalog,
    hasDailyServiceForm,
    canViewLastReport,
    isOutOfService,
    onBeginInspection,
    onRecordDailyService,
    onViewLastReport,
    onViewReportHistory,
    onChangeUnit,
    showChangeUnit,
}: InspectionOptionsHubProps) => {
    const operationalStatus = resolveOperationalStatus(primaryDiscrepancyStatus);
    const umLabel = displayUnitOfMeasure(selectedUnit.unitOfMeasure, umCatalog) || 'hrs';
    const lastUmReading = formatUmReading(selectedUnit.currentUMCounterValue);
    const umSinceService = resolveUmSinceLastService(selectedUnit);
    const umSinceServiceFormatted =
        umSinceService != null ? formatUmReading(umSinceService, umSinceService % 1 === 0 ? 0 : 1) : null;
    const lastInspectedLabel = formatInspectionTimestamp(lastInspectedAt);
    const unitHeading = selectedUnit.name || 'Unit';
    const unitSubtitleLine =
        categoryLabel && categoryLabel.trim() !== unitHeading.trim()
            ? `${unitHeading} - ${categoryLabel}`
            : selectedUnit.description && selectedUnit.description.trim() !== unitHeading.trim()
              ? selectedUnit.description.trim().startsWith(unitHeading.trim())
                  ? selectedUnit.description.trim()
                  : `${unitHeading} - ${selectedUnit.description.trim()}`
              : null;

    const metaParts = [locationLabel].filter(Boolean);

    return (
        <div className="inspection-flow-hub">
            <Card
                variant="card-lg"
                className={clsx(
                    'inspection-flow-summary card-pad-lg',
                    primaryDiscrepancyStatus === ITEM_STATUS_CODES.inop && 'has-issue',
                    primaryDiscrepancyStatus === ITEM_STATUS_CODES.under_review && 'has-under-review',
                )}
            >
                <div className="inspection-flow-summary-header">
                    <div className="inspection-flow-summary-leading">
                        <div className="inspection-flow-summary-image-wrap">
                            {imageUrl ? (
                                <img src={imageUrl} alt="" className="inspection-flow-summary-image" />
                            ) : (
                                <div className="inspection-flow-summary-image-placeholder" aria-hidden="true" />
                            )}
                        </div>
                        <div className="inspection-flow-summary-text">
                            <h1 className="inspection-flow-summary-title">{unitHeading}</h1>
                            {unitSubtitleLine && (
                                <p className="inspection-flow-summary-subtitle">{unitSubtitleLine}</p>
                            )}
                            <p className="inspection-flow-summary-meta">
                                {metaParts.join(' · ')}
                                {metaParts.length > 0 ? (
                                    <>
                                        {' · '}
                                        <span
                                            className={clsx(
                                                'inspection-flow-status',
                                                operationalStatusClass[operationalStatus],
                                            )}
                                        >
                                            <span className="inspection-flow-status-dot" aria-hidden="true" />
                                            {INSPECTION_OPERATIONAL_STATUS_LABEL[operationalStatus]}
                                        </span>
                                    </>
                                ) : (
                                    <span
                                        className={clsx(
                                            'inspection-flow-status',
                                            operationalStatusClass[operationalStatus],
                                        )}
                                    >
                                        <span className="inspection-flow-status-dot" aria-hidden="true" />
                                        {INSPECTION_OPERATIONAL_STATUS_LABEL[operationalStatus]}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    {lastInspectedLabel && (
                        <span className="inspection-flow-last-inspected-badge">
                            Last inspected: {lastInspectedLabel}
                        </span>
                    )}
                </div>

                {showChangeUnit && onChangeUnit && (
                    <div className="inspection-flow-change-unit-row">
                        <Btn
                            variant="secondary-soft-outlined"
                            buttonText="Change unit"
                            buttonOnClick={onChangeUnit}
                        />
                    </div>
                )}

                <div className="inspection-flow-stats">
                    <div className="inspection-flow-stat">
                        <span className="inspection-flow-stat-label">Last UM reading</span>
                        <span className="inspection-flow-stat-value">
                            {lastUmReading ? `${lastUmReading} ${umLabel}` : '—'}
                        </span>
                    </div>
                    <div className="inspection-flow-stat">
                        <span className="inspection-flow-stat-label">UM since last service</span>
                        <span className="inspection-flow-stat-value">
                            {umSinceServiceFormatted ?? '—'}
                        </span>
                    </div>
                    <div className="inspection-flow-stat">
                        <span className="inspection-flow-stat-label">Last inspector</span>
                        <span className="inspection-flow-stat-value">{lastInspectorName ?? '—'}</span>
                    </div>
                    <div className="inspection-flow-stat">
                        <span className="inspection-flow-stat-label">Open issues</span>
                        <span
                            className={clsx(
                                'inspection-flow-stat-value',
                                openIssueCount === 0 && 'inspection-flow-stat-value-positive',
                            )}
                        >
                            {openIssueCount === 0 ? 'None' : openIssueCount}
                        </span>
                    </div>
                </div>
            </Card>

            <div className="inspection-flow-actions">
                <InspectionActionCard
                    title="Begin Inspection"
                    description="Complete the full equipment inspection checklist. Estimated 5–10 min."
                    icon={<IconClipboard iconClasses="inspection-flow-action-svg" />}
                    tone="navy"
                    disabled={!equipmentData || isOutOfService}
                    onClick={onBeginInspection}
                />
                <InspectionActionCard
                    title="Record Daily Service Log"
                    description="Log fluids, consumables, and daily service items for this equipment."
                    icon={<IconForms iconClasses="inspection-flow-action-svg" />}
                    tone="coral"
                    disabled={!hasDailyServiceForm || isOutOfService}
                    onClick={onRecordDailyService}
                />
                <InspectionActionCard
                    title="View Last Report"
                    description="Review the most recent completed inspection for this equipment."
                    icon={<IconReports iconClasses="inspection-flow-action-svg" />}
                    tone="blue"
                    disabled={!canViewLastReport}
                    onClick={onViewLastReport}
                />
                <InspectionActionCard
                    title="View Report History"
                    description="Browse past inspection reports and service logs for this equipment."
                    icon={<IconCalendar iconClasses="inspection-flow-action-svg" />}
                    tone="green"
                    onClick={onViewReportHistory}
                />
            </div>
        </div>
    );
};
